export interface SerialLatestIntentSnapshot {
  confirmedIds: number[];
  intendedIds: number[];
  isPending: boolean;
}

interface SerialLatestIntentSetOptions {
  initialIds: readonly number[];
  save: (ids: number[]) => Promise<number[]>;
  onProjectionChange: (snapshot: SerialLatestIntentSnapshot) => void;
  onError: (error: unknown) => void;
}

export interface SerialLatestIntentSet {
  getSnapshot: () => SerialLatestIntentSnapshot;
  setIntendedIds: (ids: readonly number[]) => void;
  syncConfirmedIds: (ids: readonly number[]) => void;
  updateCallbacks: (callbacks: {
    save: (ids: number[]) => Promise<number[]>;
    onError: (error: unknown) => void;
  }) => void;
}

export function createSerialLatestIntentSet({
  initialIds,
  save,
  onProjectionChange,
  onError,
}: SerialLatestIntentSetOptions): SerialLatestIntentSet {
  let confirmedIds = normalizeIds(initialIds);
  let intendedIds = [...confirmedIds];
  let isPending = false;
  let queuedIds: number[] | null = null;
  let saveCallback = save;
  let errorCallback = onError;

  function getSnapshot(): SerialLatestIntentSnapshot {
    return {
      confirmedIds: [...confirmedIds],
      intendedIds: [...intendedIds],
      isPending,
    };
  }

  function notify(): void {
    onProjectionChange(getSnapshot());
  }

  async function persist(ids: number[]): Promise<void> {
    isPending = true;
    notify();
    try {
      confirmedIds = normalizeIds(await saveCallback([...ids]));
    } catch (error) {
      intendedIds = [...confirmedIds];
      notify();
      errorCallback(error);
    } finally {
      isPending = false;
      const nextIds = queuedIds;
      queuedIds = null;
      if (nextIds != null && !sameIds(nextIds, confirmedIds)) {
        intendedIds = [...nextIds];
        notify();
        void persist(nextIds);
      } else {
        intendedIds = [...confirmedIds];
        notify();
      }
    }
  }

  return {
    getSnapshot,
    setIntendedIds(ids) {
      const nextIds = normalizeIds(ids);
      intendedIds = [...nextIds];
      notify();
      if (isPending) {
        queuedIds = nextIds;
        return;
      }
      if (!sameIds(nextIds, confirmedIds)) void persist(nextIds);
    },
    syncConfirmedIds(ids) {
      if (isPending) return;
      confirmedIds = normalizeIds(ids);
      intendedIds = [...confirmedIds];
      notify();
    },
    updateCallbacks(callbacks) {
      saveCallback = callbacks.save;
      errorCallback = callbacks.onError;
    },
  };
}

function normalizeIds(ids: readonly number[]): number[] {
  return [...new Set(ids)].sort((a, b) => a - b);
}

function sameIds(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
